// Push notification helpers — subscribe + unsubscribe the current device.
// All calls assume a registered service worker; safe no-ops otherwise.

import { api } from "@/lib/api";

const STORAGE_KEY = "ts-push-asked";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Standard = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64Standard);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export function pushAskedBefore() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(STORAGE_KEY) === "1";
}

export function markPushAsked() {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, "1");
}

async function getReadyRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  // serviceWorker.ready resolves only after activation. In dev (no SW registered)
  // this would hang forever, so race with a tiny timeout.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) => setTimeout(() => resolve(null), 1500)),
  ]);
}

/** Ask the user for permission and subscribe to push. Returns the
 * PushSubscription on success, or null on failure / refusal.
 */
export async function enablePushNotifications() {
  if (!pushSupported()) return null;
  markPushAsked();

  // 1. Permission gate.
  let perm = Notification.permission;
  if (perm === "default") {
    try {
      perm = await Notification.requestPermission();
    } catch {
      return null;
    }
  }
  if (perm !== "granted") return null;

  // 2. Need a registered service worker.
  const reg = await getReadyRegistration();
  if (!reg) return null;

  // 3. Need the server's VAPID public key.
  let publicKey = "";
  try {
    const { data } = await api.get("/push/public-key");
    if (!data?.enabled || !data?.public_key) return null;
    publicKey = data.public_key;
  } catch {
    return null;
  }

  // 4. Subscribe (or reuse existing subscription).
  let sub;
  try {
    sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
  } catch (err) {
    console.error("[push] subscribe failed", err);
    return null;
  }

  // 5. Persist on the server.
  try {
    await api.post("/push/subscribe", { subscription: sub.toJSON() });
  } catch (err) {
    console.error("[push] server subscribe failed", err);
    return null;
  }

  return sub;
}

export async function disablePushNotifications() {
  if (!pushSupported()) return false;
  const reg = await getReadyRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  try {
    await api.post("/push/unsubscribe", { subscription: sub.toJSON() });
  } catch {
    /* ignore — still try to unsub locally */
  }
  try {
    await sub.unsubscribe();
  } catch {
    return false;
  }
  return true;
}

export async function isPushSubscribed() {
  if (!pushSupported() || Notification.permission !== "granted") return false;
  const reg = await getReadyRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}
